<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openCreate">
          <i class="ri-add-line"></i>
          新增分類
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="page-toolbar">
        <div class="filter-group">
          <select v-model="filterActive" class="form-control" @change="fetchCategories">
            <option value="">全部狀態</option>
            <option value="true">啟用</option>
            <option value="false">停用</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchCategories">重試</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>排序</th>
              <th>分類名稱</th>
              <th>說明</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredCategories.length === 0">
              <td colspan="5" class="empty-state">尚無分類，請新增</td>
            </tr>
            <tr v-for="cat in filteredCategories" :key="cat.id">
              <td>{{ cat.sortOrder }}</td>
              <td><strong>{{ cat.name }}</strong></td>
              <td>{{ cat.description || '-' }}</td>
              <td>
                <span :class="['badge', cat.isActive ? 'badge-success' : 'badge-secondary']">
                  {{ cat.isActive ? '啟用' : '停用' }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <button class="btn btn-sm btn-secondary" @click="openEdit(cat)">
                    <i class="ri-edit-line"></i>
                    編輯
                  </button>
                  <button
                    v-if="cat.isActive"
                    class="btn btn-sm btn-warning"
                    @click="handleToggle(cat, false)"
                  >
                    停用
                  </button>
                  <button
                    v-else
                    class="btn btn-sm btn-success"
                    @click="handleToggle(cat, true)"
                  >
                    啟用
                  </button>
                  <button class="btn btn-sm btn-danger" @click="handleDelete(cat)">
                    <i class="ri-delete-bin-line"></i>
                    刪除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="modalOpen" class="modal-mask" @click.self="closeModal">
      <div class="modal-card">
        <h3>{{ editing ? '編輯分類' : '新增分類' }}</h3>
        <div class="form-group">
          <label class="form-label required">分類名稱</label>
          <input v-model="form.name" type="text" class="form-control" maxlength="50" />
        </div>
        <div class="form-group">
          <label class="form-label">說明</label>
          <textarea v-model="form.description" class="form-control" rows="3" maxlength="500" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">排序</label>
            <input v-model.number="form.sortOrder" type="number" class="form-control" min="0" max="9999" />
          </div>
          <div class="form-group">
            <label class="form-label">狀態</label>
            <select v-model="form.isActive" class="form-control">
              <option :value="true">啟用</option>
              <option :value="false">停用</option>
            </select>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" :disabled="saving" @click="closeModal">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="handleSave">
            {{ saving ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { supportService } from '@/api/modules/support.js';
import { showError, showSuccess, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'SupportTicketCategories',
  setup() {
    const { pageTitle } = usePageTitle('工單分類');
    const categories = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const filterActive = ref('');
    const modalOpen = ref(false);
    const editing = ref(null);
    const saving = ref(false);
    const form = reactive({
      name: '',
      description: '',
      sortOrder: 0,
      isActive: true
    });

    const filteredCategories = computed(() => {
      if (filterActive.value === '') return categories.value;
      const active = filterActive.value === 'true';
      return categories.value.filter((c) => c.isActive === active);
    });

    const fetchCategories = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await supportService.listTicketCategories();
        if (response?.success) {
          categories.value = response.data || [];
        } else {
          error.value = response?.message || '載入失敗';
        }
      } catch (err) {
        error.value = err.message || '載入失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const resetForm = () => {
      form.name = '';
      form.description = '';
      form.sortOrder = (categories.value.length + 1) * 10;
      form.isActive = true;
    };

    const openCreate = () => {
      editing.value = null;
      resetForm();
      modalOpen.value = true;
    };

    const openEdit = (cat) => {
      editing.value = cat;
      form.name = cat.name;
      form.description = cat.description || '';
      form.sortOrder = cat.sortOrder ?? 0;
      form.isActive = !!cat.isActive;
      modalOpen.value = true;
    };

    const closeModal = () => {
      modalOpen.value = false;
      editing.value = null;
    };

    const handleSave = async () => {
      if (!form.name.trim()) {
        await showError('請填寫分類名稱');
        return;
      }
      saving.value = true;
      try {
        const payload = {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
          isActive: !!form.isActive
        };
        let response;
        if (editing.value) {
          response = await supportService.updateTicketCategory(editing.value.id, payload);
        } else {
          response = await supportService.createTicketCategory(payload);
        }
        if (response?.success) {
          await showSuccess(editing.value ? '已更新' : '已建立');
          closeModal();
          await fetchCategories();
        } else {
          await showError('儲存失敗', response?.message || '無法儲存分類');
        }
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message);
      } finally {
        saving.value = false;
      }
    };

    const handleToggle = async (cat, isActive) => {
      try {
        await supportService.updateTicketCategory(cat.id, { isActive });
        await showSuccess(isActive ? '已啟用' : '已停用');
        await fetchCategories();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      }
    };

    const handleDelete = async (cat) => {
      const ok = await showDeleteConfirm(cat.name, '若已有工單使用此分類，將改為停用而非刪除。');
      if (!ok) return;
      try {
        const response = await supportService.deleteTicketCategory(cat.id);
        await showSuccess(response?.message || '已處理');
        await fetchCategories();
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message);
      }
    };

    onMounted(fetchCategories);

    return {
      pageTitle,
      categories,
      filteredCategories,
      loading,
      error,
      filterActive,
      modalOpen,
      editing,
      saving,
      form,
      fetchCategories,
      openCreate,
      openEdit,
      closeModal,
      handleSave,
      handleToggle,
      handleDelete
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 720px;
}

.form-label.required::after {
  content: ' *';
  color: #e03131;
}

.form-group {
  margin-bottom: 14px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-card {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 480px;
}

.modal-card h3 {
  margin: 0 0 16px;
  font-size: 18px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn-warning {
  background: #fab005;
  color: #212529;
  border: none;
}

.btn-success {
  background: #40c057;
  color: #fff;
  border: none;
}
</style>
